import React from "react"
import * as Sentry from "@sentry/browser"
import { Redirect } from "react-router-dom"

import { useAssertRootQueryLoaded, CurrentUser } from "graphql/smart-queries/user"
import { baseRoutes } from "routes"
import { Journey, NextActionEnum, JourneyKindEnum } from "typings/types"

type JourneyWithNextActions = Pick<Journey, "nextActions" | "kind">

export const userShouldGoToCheckout = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey &&
  journey.nextActions.includes(NextActionEnum.Checkout) &&
  [JourneyKindEnum.CovePurchase, JourneyKindEnum.CoveECommerce, JourneyKindEnum.CoveLtdd].includes(journey.kind)

export const userShouldGoToAcceptLegals = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey && journey.nextActions.includes(NextActionEnum.AcceptLegals) && journey.kind !== JourneyKindEnum.CoveRenewal

export const userShouldGoToCompleteQuiz = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey &&
  journey.nextActions.includes(NextActionEnum.UpdateQuizResponse) &&
  journey.kind !== JourneyKindEnum.CoveRenewal

export const userShouldGoToPhotoUpload = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey && journey.nextActions.includes(NextActionEnum.UpdatePhoto)

export const userShouldGoToApproveTreatment = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey && journey.nextActions.includes(NextActionEnum.ApproveTreatment)

export const userShouldCompleteAnnualRenewal = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey &&
  journey.nextActions.some(action => [NextActionEnum.StartQuiz, NextActionEnum.UpdateQuizResponse].includes(action)) &&
  journey.kind === JourneyKindEnum.CoveRenewal

export const renewalConsultationPending = (journey: Maybe<JourneyWithNextActions>): boolean =>
  !!journey &&
  journey.nextActions.includes(NextActionEnum.SuggestTreatment) &&
  journey.kind === JourneyKindEnum.CoveRenewal

type RedirectDetails = {
  isLead: boolean
  isUser: boolean
  origin: string
}
type RedirectResult = {
  nextRoute: string
  details: RedirectDetails
}

export const findRedirectPath = (user: Maybe<CurrentUser>): RedirectResult => {
  const isUser = !!user && user.active
  const isLead = !!user && !user.active
  let nextRoute = baseRoutes.purchase()

  const details = {
    isLead,
    isUser,
    origin: window?.location.href
  }

  if (isUser) {
    const journey = user!.activeJourneys[0]
    const subscription = user!.subscriptions[0]
    const kind = journey ? journey.kind : undefined
    const isOnboardingJourney =
      journey &&
      [JourneyKindEnum.CovePurchase, JourneyKindEnum.CoveECommerce, JourneyKindEnum.CoveLtdd].includes(journey.kind)

    if (isOnboardingJourney) {
      /* This means that the user is in the middle of the first-time journey */
      if (userShouldGoToCheckout(journey)) {
        nextRoute = baseRoutes.checkout(kind)
      } else if (userShouldGoToAcceptLegals(journey)) {
        nextRoute = baseRoutes.confirmTerms(kind)
      } else if (userShouldGoToCompleteQuiz(journey)) {
        /* Inside-quiz redirects are handled in the QuizWayfinder */
        nextRoute = baseRoutes.consultationQuiz(kind)
      } else {
        nextRoute = baseRoutes.dashboard()
      }
    } else if (journey && journey.kind === JourneyKindEnum.CoveUpdateTreatment) {
      nextRoute = baseRoutes.dashboard()
    } else if (!subscription) {
      /* This means that the user has never checkout (because we create the journey on checkout) */
      nextRoute = baseRoutes.checkout(kind)
    } else {
      /* This means that the user has a subscription */
      nextRoute = baseRoutes.dashboard()
    }
  } else if (isLead) {
    nextRoute = baseRoutes.createAccount()
  }

  return { nextRoute, details }
}

const Wayfinder: React.FC = () => {
  const { user } = useAssertRootQueryLoaded()
  const { nextRoute, details } = findRedirectPath(user)

  /* Find the user position in the flow and redirect him to the first unfinished step */
  Sentry.withScope(scope => {
    /**
     * We configure Sentry scope user in the background
     * This code is executed before that so we need to additionally call scope.setUser
     */
    if (user) {
      scope.setUser({
        email: user.email,
        id: String(user.id)
      })
    }

    scope.setExtra("isLead", details.isLead.toString())
    scope.setExtra("isUser", details.isUser.toString())
    scope.setExtra("origin", details.origin)
    scope.setExtra("nextRoute", nextRoute)
    Sentry.captureMessage("Redirecting user...")
  })

  return <Redirect to={nextRoute} />
}

export default Wayfinder
